import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { PullDataService } from '../../service/PullDataService';
import { useAuthStore } from '../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import SearchableSelect from '../SearchableSelect';
import TagInput from '../Test/TagInput';

const LoadingPlaceholder = ({
  theme,
  label,
}: {
  theme?: string;
  label: string;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '8px' }}>
    <CircularProgress
      size={16}
      sx={{ color: theme === 'dark' ? '#D0D5DD' : '#667085' }}
    />
    <Typography
      sx={{ fontSize: '13px', color: theme === 'dark' ? '#98A2B3' : '#667085' }}
    >
      {label}
    </Typography>
  </Box>
);

interface MicrosoftFile {
  id: string;
  name: string;
}

interface MicrosoftWorksheet {
  id: string;
  name: string;
  position: number;
}

export interface MicrosoftExcelSelectProps {
  formData: any;
  onChange: (e: { target: { name: string; value: any } }) => void;
  onChangeSelect: (e: { target: { name: string; value: any } }) => void;
  data: any;
  nodeId: string;
  nodeLabel: string;
  /** When true, renders the optional range filter TagInput (Pull Data use case). */
  showRangeFilter?: boolean;
  /** When true, adds "Create New Sheet" option and New Sheet Name field (Push Data use case). */
  showNewSheetOption?: boolean;
}

const DEBOUNCE_MS = 400;

const MicrosoftExcelSelect: React.FC<MicrosoftExcelSelectProps> = ({
  formData,
  onChange,
  onChangeSelect,
  data,
  nodeId,
  nodeLabel,
  showRangeFilter = false,
  showNewSheetOption = false,
}) => {
  const { user } = useAuthStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);

  const [excelFiles, setExcelFiles] = useState<MicrosoftFile[]>([]);
  const [worksheets, setWorksheets] = useState<MicrosoftWorksheet[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<string>(
    data?.microsoftFileId ?? '',
  );
  const [selectedWorksheetId, setSelectedWorksheetId] = useState<string>(
    data?.microsoftWorksheetId ?? '',
  );
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingWorksheets, setLoadingWorksheets] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadExcelFiles = useCallback(
    async (search?: string) => {
      setLoadingFiles(true);
      try {
        const response = await PullDataService.getMicrosoftExcelFiles(
          search ? { search } : undefined,
        );
        setExcelFiles(response.data.files ?? []);
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      } finally {
        setLoadingFiles(false);
        setInitialLoadDone(true);
      }
    },
    [setNotification],
  );

  const loadWorksheets = useCallback(
    async (fileId: string) => {
      setLoadingWorksheets(true);
      try {
        const response = await PullDataService.getMicrosoftExcelWorksheets({
          fileId,
        });
        setWorksheets(response.data.worksheets ?? []);
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      } finally {
        setLoadingWorksheets(false);
      }
    },
    [setNotification],
  );

  useEffect(() => {
    loadExcelFiles();
  }, [loadExcelFiles]);

  useEffect(() => {
    if (selectedFileId) {
      loadWorksheets(selectedFileId);
    }
  }, [selectedFileId, loadWorksheets]);

  const handleFileInputChange = (
    _event: React.SyntheticEvent,
    inputValue: string,
    reason: string,
  ) => {
    if (reason !== 'input' && reason !== 'clear') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadExcelFiles(inputValue || undefined);
    }, DEBOUNCE_MS);
  };

  const fileOptions = excelFiles.map(f => ({ label: f.name, value: f.id }));
  const worksheetOptionsFromApi = worksheets.map(w => ({
    label: w.name,
    value: w.id,
  }));
  const worksheetOptions = showNewSheetOption
    ? [{ label: 'Create New Sheet', value: 'new' }, ...worksheetOptionsFromApi]
    : worksheetOptionsFromApi;

  const searchableSelectSx = {
    '& .MuiList-root': {
      backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    },
    '& .MuiSvgIcon-root': {
      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
    },
    borderRadius: '8px',
    border: 'none',
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
  };

  const menuPropsSx = {
    PaperProps: {
      sx: {
        '& .Mui-selected': {
          backgroundColor: user?.theme === 'dark' ? '#667085' : '#D0D5DD',
        },
        backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
      },
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        minWidth: '360px',
      }}
    >
      {loadingFiles && !initialLoadDone ? (
        <LoadingPlaceholder theme={user?.theme} label="Loading files..." />
      ) : (
        <>
          <SearchableSelect
            options={fileOptions}
            label="Search or select an Excel file"
            loading={loadingFiles}
            loadingText="Searching..."
            onInputChange={handleFileInputChange}
            filterOptions={x => x}
            onChange={value => {
              const fileId = value ? value.value : '';
              setSelectedFileId(fileId);
              setSelectedWorksheetId('');
              setWorksheets([]);
              setTimeout(() => {
                onChangeSelect({
                  target: { name: 'microsoftFileId', value: fileId },
                });
              }, 0);
            }}
            value={fileOptions.find(o => o.value === selectedFileId) || null}
            className="nodrag"
            fullWidth
            sx={searchableSelectSx}
            MenuProps={menuPropsSx}
          />

          {loadingWorksheets ? (
            <LoadingPlaceholder
              theme={user?.theme}
              label="Loading worksheets..."
            />
          ) : (
            <>
              <SearchableSelect
                options={worksheetOptions}
                label="Select a worksheet"
                onChange={value => {
                  const worksheetId = value ? value.value : '';
                  setSelectedWorksheetId(worksheetId);
                  setTimeout(() => {
                    onChangeSelect({
                      target: {
                        name: 'microsoftWorksheetId',
                        value: worksheetId,
                      },
                    });
                  }, 0);
                }}
                value={
                  worksheetOptions.find(
                    o => o.value === selectedWorksheetId,
                  ) || null
                }
                fullWidth
                sx={{
                  ...searchableSelectSx,
                  bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                }}
                MenuProps={menuPropsSx}
              />

              {showNewSheetOption && selectedWorksheetId === 'new' && (
                <TagInput
                  placeholder="Enter new sheet name"
                  onChange={onChange}
                  name="name"
                  value={formData.name ?? ''}
                  isAdvanced
                  labelName="New Sheet Name"
                  isCollapsed={false}
                  handleId="sheetName"
                  nodeId={nodeId}
                  dataConnected={data?.sheetNameData || []}
                  advancedCollapsed
                  nodeLabel={nodeLabel}
                  labelStyle={{
                    fontSize: '14px',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    fontWeight: 400,
                  }}
                />
              )}

              {showRangeFilter && (
                <TagInput
                  placeholder="Use A1 Notation. Example: A1:Z40"
                  onChange={onChange}
                  name="rangeFilter"
                  value={formData.rangeFilter ?? ''}
                  isAdvanced
                  showCopy
                  dataConnected={data?.rangeFilterData || []}
                  removeIcons
                  labelName={'Select Area (Optional)'}
                  isCollapsed
                  nodeId={nodeId}
                  handleId="rangeFilter"
                  nodeLabel={nodeLabel}
                  labelStyle={{
                    fontSize: '14px',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    fontWeight: 400,
                  }}
                  advancedCollapsed
                />
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default MicrosoftExcelSelect;
